/* AMD Practitioner listings — opens and closes the "add new" modal.

   Deliberately small: the form inside the dialog is an <amd-contact-form>, so
   validation, the aria-live summary and the FormData submit all come from
   assets/amd-contact-form.js. Nothing is duplicated here.

   Uses a native <dialog>, so the focus trap, Esc and focus restore are free.

   It also fills the State select from the Country select. Shopify's
   `country_option_tags` ships each country's provinces in a data-provinces
   attribute, so the real list comes from the platform and needs no upkeep.
   Countries with no provinces hide the State field and drop its `required`, so
   validation cannot block on a select with nothing to choose. */

class AmdPractitionerListings extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onCountryChange = this._onCountryChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);

    this.country = this.querySelector('[data-amd-pl-country]');
    if (this.country) {
      this.country.addEventListener('change', this._onCountryChange);
      this._syncStates();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this.country) this.country.removeEventListener('change', this._onCountryChange);
  }

  _onCountryChange() {
    this._syncStates();
  }

  /* Rebuild the State options from the selected country's data-provinces. */
  _syncStates() {
    const state = this.querySelector('[data-amd-pl-state]');
    const field = this.querySelector('[data-amd-pl-state-field]');
    if (!state) return;

    const option = this.country.selectedOptions[0];
    let provinces = [];
    try {
      provinces = JSON.parse(option?.dataset.provinces || '[]');
    } catch {
      provinces = [];
    }

    const placeholder = state.querySelector('option[value=""]');
    state.textContent = '';
    if (placeholder) state.appendChild(placeholder);

    provinces.forEach(([code, name]) => {
      const opt = document.createElement('option');
      // Store the human name: it is what lands in the notification email.
      opt.value = name;
      opt.textContent = name;
      opt.dataset.code = code;
      state.appendChild(opt);
    });

    // The field is ALWAYS visible, like the legacy form: it shows "Select State"
    // from the start. Only a country that genuinely has no provinces disables
    // it, and then `required` comes off so validation cannot block on a select
    // with nothing to choose. Hiding it on load was wrong — no country is
    // selected yet at that point.
    const hasProvinces = provinces.length > 0;
    const countryChosen = Boolean(this.country.value);

    state.disabled = countryChosen && !hasProvinces;
    if (hasProvinces || !countryChosen) {
      state.setAttribute('required', '');
    } else {
      state.removeAttribute('required');
      state.removeAttribute('aria-invalid');
    }
    if (field) field.hidden = false;
  }

  _onClick(event) {
    const opener = event.target.closest('[data-amd-pl-open]');
    if (opener && this.contains(opener)) {
      const dialog = this.querySelector('#' + window.CSS.escape(opener.getAttribute('data-amd-pl-open')));
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
        const first = dialog.querySelector('input, textarea, select');
        if (first) first.focus();
      }
      return;
    }

    const closer = event.target.closest('[data-amd-pl-close]');
    if (closer && this.contains(closer)) {
      const dialog = closer.closest('[data-amd-pl-dialog]');
      if (dialog) dialog.close();
      return;
    }

    // A hit on the dialog element itself, and not on its contents, is the
    // backdrop. The scrolling body fills the dialog, so this stays reliable.
    const dialog = event.target.closest('[data-amd-pl-dialog]');
    if (dialog && event.target === dialog) dialog.close();
  }
}

if (!customElements.get('amd-practitioner-listings')) {
  customElements.define('amd-practitioner-listings', AmdPractitionerListings);
}
